/**
 * AccountPrivacy
 *
 * Single page that surfaces every user-rights action in one place:
 *   • Download my data (right to access / portability — GDPR Art. 15, CCPA §1798.100)
 *   • Delete my account (right to erasure — GDPR Art. 17, CCPA §1798.105)
 *   • Marketing email opt-out
 *   • Do Not Sell or Share My Personal Information (CCPA §1798.135)
 *   • Reset cookie consent
 *
 * Linked from the footer and from the new account-level "Privacy & Data"
 * nav item. Requires authentication — anonymous visitors are redirected
 * to login with `next=/account/privacy` so we land them right back here.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ShieldCheck, Download, Trash2, MailX, Ban,
    Cookie, Loader2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import PublicLayout from "@/components/layout/PublicLayout";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

export default function AccountPrivacy() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [dnsState, setDnsState] = useState(null);  // { opted_out, opted_out_at }
    const [busy, setBusy] = useState("");
    const [marketingOptedOut, setMarketingOptedOut] = useState(null);

    // Auth gate.
    useEffect(() => {
        if (!authLoading && !user) {
            navigate(
                `/auth/login?next=${encodeURIComponent("/account/privacy")}`,
                { replace: true },
            );
        }
    }, [user, authLoading, navigate]);

    // Initial state load.
    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const r = await api.get("/account/do-not-sell");
                setDnsState(r.data);
            } catch { /* non-critical */ }
            try {
                const r = await api.get("/account/me");
                const prefs = r.data?.notification_prefs || r.data || {};
                setMarketingOptedOut(prefs.email_opt_in === false);
            } catch { /* non-critical */ }
        })();
    }, [user]);

    // Deep-link from footer "Do Not Sell" — scroll into view.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("action") === "do-not-sell") {
            setTimeout(() => {
                document.getElementById("do-not-sell")?.scrollIntoView({
                    behavior: "smooth", block: "start",
                });
            }, 250);
        }
    }, [location.search]);

    const exportData = async () => {
        setBusy("export");
        try {
            const r = await api.get("/account/export");
            const blob = new Blob([JSON.stringify(r.data, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `buyers-leverage-data-export-${new Date()
                .toISOString().slice(0, 10)}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Data export downloaded.");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not generate export.",
            );
        } finally {
            setBusy("");
        }
    };

    const requestDeletion = async () => {
        if (!window.confirm(
            "Request account deletion?\n\n" +
            "We'll erase your reports, uploads, transactions, and audit " +
            "log within 30 days. This cannot be undone.",
        )) return;
        const confirmation = window.prompt(
            'Type "DELETE" (in caps) to confirm:',
        );
        if (confirmation !== "DELETE") {
            toast.error("Confirmation text did not match.");
            return;
        }
        setBusy("delete");
        try {
            await api.post("/account/deletion-request", { confirmation: "DELETE" });
            toast.success("Deletion requested. We'll email confirmation.");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not submit request.",
            );
        } finally {
            setBusy("");
        }
    };

    const setMarketingOpt = async (optOut) => {
        setBusy("marketing");
        try {
            await api.patch("/account/notifications", {
                email_opt_in: !optOut,
            });
            setMarketingOptedOut(optOut);
            toast.success(
                optOut
                    ? "Opted out of marketing emails."
                    : "Marketing emails resumed.",
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not update preference.",
            );
        } finally {
            setBusy("");
        }
    };

    const setDoNotSell = async (optOut) => {
        setBusy("dns");
        try {
            const r = await api.post("/account/do-not-sell", {
                opt_in: !optOut,
            });
            setDnsState(r.data);
            toast.success(
                optOut
                    ? "Opt-out recorded. We will not sell or share your data."
                    : "Opt-out reverted.",
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not update preference.",
            );
        } finally {
            setBusy("");
        }
    };

    const resetCookieConsent = () => {
        try {
            localStorage.removeItem("bl_cookie_consent_v1");
            localStorage.removeItem("bl_cookie_consent_at");
            toast.success("Cookie banner will reappear on next page load.");
        } catch {
            toast.error("Could not reset cookie consent.");
        }
    };

    if (authLoading || !user) {
        return (
            <PublicLayout>
                <div className="bl-container py-20 text-center text-bl-muted">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                    Loading…
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-16 pb-10">
                    <div className="bl-eyebrow">
                        <ShieldCheck className="w-3.5 h-3.5" /> Account
                    </div>
                    <h1 className="mt-5 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight text-bl">
                        Your Privacy &amp; Data
                    </h1>
                    <p className="mt-5 max-w-2xl text-bl-muted leading-relaxed">
                        Every right under CCPA and GDPR, in one place. Download
                        every record we hold on you, request deletion, opt out
                        of marketing emails or data sharing, or reset your
                        cookie consent — all without leaving the page.
                    </p>
                </div>
            </section>

            <section className="bl-container py-12 max-w-3xl space-y-6">
                <RightsCard
                    icon={Download}
                    title="Download my data"
                    description="Get a machine-readable JSON export of your profile, reports, uploads, transactions, audit log, and entitlements. Right to access (GDPR Art. 15, CCPA §1798.100)."
                    cta="Download export"
                    onClick={exportData}
                    busy={busy === "export"}
                    testid="privacy-export"
                />
                <RightsCard
                    icon={MailX}
                    title="Marketing emails"
                    description="Transactional emails (receipts, password resets, account alerts) always send. Marketing emails (tips, lifecycle reminders) can be turned off here."
                    cta={marketingOptedOut ? "Resume marketing emails" : "Opt out of marketing"}
                    badge={marketingOptedOut ? "opted out" : null}
                    onClick={() => setMarketingOpt(!marketingOptedOut)}
                    busy={busy === "marketing"}
                    testid="privacy-marketing"
                />
                <div id="do-not-sell">
                    <RightsCard
                        icon={Ban}
                        title="Do not sell or share my personal information"
                        description="Required by CCPA §1798.135. We do not sell user data, but you can lock the opt-out into the record so it is auditable. Honored across every back-end pipeline."
                        cta={dnsState?.opted_out ? "Revert opt-out" : "Opt out now"}
                        badge={dnsState?.opted_out ? "opted out" : null}
                        onClick={() => setDoNotSell(!dnsState?.opted_out)}
                        busy={busy === "dns"}
                        testid="privacy-do-not-sell"
                    />
                </div>
                <RightsCard
                    icon={Cookie}
                    title="Reset cookie consent"
                    description="Clear your saved cookie choice and re-show the consent banner on the next page load. Useful if your preference has changed."
                    cta="Reset banner"
                    onClick={resetCookieConsent}
                    testid="privacy-cookie-reset"
                />
                <RightsCard
                    icon={Trash2}
                    title="Delete my account"
                    description="Right to erasure (GDPR Art. 17, CCPA §1798.105). We will erase your reports, uploads, transactions, and audit log within 30 days. Backups purged within 90 days. This cannot be undone."
                    cta="Request deletion"
                    danger
                    onClick={requestDeletion}
                    busy={busy === "delete"}
                    testid="privacy-delete"
                />
            </section>
        </PublicLayout>
    );
}

function RightsCard({
    icon: Icon,
    title,
    description,
    cta,
    onClick,
    busy = false,
    danger = false,
    badge = null,
    testid,
}) {
    return (
        <article
            className="bl-card p-6 sm:p-7 flex items-start gap-4"
            data-testid={testid}
        >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--bl-tint)] text-bl-primary shrink-0">
                <Icon className="w-5 h-5" />
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-display font-semibold text-lg text-bl">
                        {title}
                    </h2>
                    {badge && (
                        <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--bl-tint)] text-bl-primary font-medium"
                            data-testid={`${testid}-badge`}
                        >
                            <CheckCircle2 className="w-3 h-3" />
                            {badge}
                        </span>
                    )}
                </div>
                <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                    {description}
                </p>
                <button
                    type="button"
                    onClick={onClick}
                    disabled={busy}
                    className={
                        danger
                            ? "mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[var(--bl-rust)] text-[var(--bl-rust)] text-sm font-medium hover:bg-[var(--bl-rust-tint)] transition-colors disabled:opacity-50"
                            : "mt-4 bl-btn-secondary text-sm"
                    }
                    data-testid={`${testid}-btn`}
                >
                    {busy ? (
                        <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Working…
                        </>
                    ) : (
                        cta
                    )}
                </button>
            </div>
        </article>
    );
}
